import { useConditionalEffect } from 'hooks/useConditionalEffect'
import { useMountEffect } from 'hooks/useMountEffect'
import {
  forwardRef,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef
} from 'react'
import { Subject } from 'rxjs'
import { debounceTime, takeUntil } from 'rxjs/operators'

export interface ScrollListenerHandle {
  emitScroll(): void
}

export interface ScrollListenerProps {
  emitTreshold: number
  emitInitial?: boolean
  className?: string
  disabled?: boolean
  useWindow?: boolean
  scrollElement?: Element
  onScrollDebounceTime?: number
  onScroll(offset: number, top: number): void
  children?: ReactNode
}

export const ScrollListener = forwardRef<ScrollListenerHandle, PropsWithChildren<ScrollListenerProps>>(
  (props, ref) => {
    const {
      emitTreshold,
      emitInitial,
      className = '',
      disabled,
      useWindow,
      scrollElement,
      onScrollDebounceTime = 100, // eslint-disable-line no-magic-numbers
      onScroll,
      children
    } = props

    const scrollComponentRef = useRef<HTMLDivElement | null>(null)
    const onScroll$ = useRef(new Subject<{ offset: number; top: number }>())
    const unmount$ = useRef(new Subject<boolean>())
    const requestAnimationFrameId = useRef<number | undefined>(undefined)
    const subscriptionRef = useRef<any>(null)
    const onScrollRef = useRef(onScroll)
    
    // Keep ref in sync with latest onScroll prop
    useEffect(() => {
      onScrollRef.current = onScroll
    }, [onScroll])

    
    const calculateTopPosition = useCallback((el: HTMLElement): number => {
      return !el ? 0 : el.offsetTop + calculateTopPosition(el.offsetParent as HTMLElement)
    }, [])

    const emitScrollCallback = useCallback(() => {
      const scrollSelf = scrollComponentRef.current

      if (!scrollSelf) {
        return
      }

      requestAnimationFrameId.current = undefined

      if (useWindow) {
        const scrollElement = window
        const scrollTop =
          scrollElement.pageYOffset !== undefined
            ? scrollElement.pageYOffset
            : (document.documentElement || (document.body.parentNode as HTMLElement) || document.body)
              .scrollTop

        const offset =
          calculateTopPosition(scrollSelf) + scrollSelf.offsetHeight - scrollTop - window.innerHeight

        if (offset < emitTreshold) {
          onScroll$.current.next({ offset, top: scrollTop })
        }

        return
      }

      const parent = scrollElement || (scrollSelf.parentNode as Element)
      const offset = scrollSelf.scrollHeight - parent.scrollTop - parent.clientHeight

      if (offset < emitTreshold) {
        onScroll$.current.next({ offset, top: parent.scrollTop })
      }
    }, [useWindow, scrollElement, emitTreshold, calculateTopPosition])

    const handleScroll = useCallback(() => {
      if (requestAnimationFrameId.current || disabled) {
        return
      }

      requestAnimationFrameId.current = requestAnimationFrame(emitScrollCallback)
    }, [disabled, emitScrollCallback])

    const attachScrollListener = useCallback(() => {
      const containerElement = scrollComponentRef.current ? scrollComponentRef.current.parentNode : null
      const scrollableElement = useWindow ? window : scrollElement || (containerElement as Element)

      if (scrollableElement) {
        scrollableElement.addEventListener('scroll', handleScroll, false)
      }
      window.addEventListener('resize', handleScroll, false)
    }, [useWindow, scrollElement, handleScroll])

    const detachScrollListener = useCallback(() => {
      const containerElement = scrollComponentRef.current ? scrollComponentRef.current.parentNode : null
      const scrollableElement = useWindow ? window : scrollElement || (containerElement as Element)

      if (scrollableElement) {
        scrollableElement.removeEventListener('scroll', handleScroll, false)
      }
      window.removeEventListener('resize', handleScroll, false)
    }, [useWindow, scrollElement, handleScroll])

    // Expose emitScroll method via imperative handle
    useImperativeHandle(ref, () => ({
      emitScroll: () => {
        emitScrollCallback()
      }
    }), [emitScrollCallback])

    // Mount effect: attach listeners and setup subscriptions
    useMountEffect(() => {
      const unmountSubject = unmount$.current

      attachScrollListener()

      subscriptionRef.current = onScroll$.current.pipe(
        debounceTime(onScrollDebounceTime),
        takeUntil(unmountSubject.asObservable())
      ).subscribe((value: { offset: number; top: number }) => {
        onScrollRef.current(value.offset, value.top)
      })

      if (emitInitial) {
        onScrollRef.current(0, 0)
      }

      return () => {
        detachScrollListener()
        if (subscriptionRef.current) {
          subscriptionRef.current.unsubscribe()
        }
        unmountSubject.next(true)
      }
    })

    // Update effect: handle prop changes for useWindow or scrollElement
    useConditionalEffect(([_attachScrollListener, _detachScrollListener]) => {
      _detachScrollListener()
      _attachScrollListener()
    }, [useWindow, scrollElement], [attachScrollListener, detachScrollListener])

    return (
      <div className={className} ref={scrollComponentRef}>
        {children}
      </div>
    )
  }
)

ScrollListener.displayName = 'ScrollListener'

export default ScrollListener
